import { faFileArrowUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ChangeEvent, useRef, useState } from 'react'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { useController, Control } from 'react-hook-form'

export interface FileSelectorProps {
  name: string
  control: Control<any>
  maxFileSize?: number
}

export const FileSelector = ({ name, control, maxFileSize = 10485760 }: FileSelectorProps) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const clickRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    defaultValue: null,
  })

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragActive(true)
    } else if (e.type === 'dragleave') {
      setIsDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      validateAndSetFile(file)
    }
  }

  const validateAndSetFile = (file: File) => {
    if (file.size > maxFileSize) {
      return
    }

    if (file.type === 'text/csv') {
      onChange(file)
    } else {
      return
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0])
    }
  }

  const addFileButtonClicked = () => {
    if (clickRef.current) {
      clickRef.current.click()
    }
  }

  return (
    <div onDragEnter={handleDrag}>
      <input
        ref={clickRef}
        type="file"
        id={`input-file-${name}`}
        multiple={false}
        className="hidden"
        onChange={handleChange}
        accept="application/vnd.ms-excel, text/csv"
      />
      <div
        className={cx(
          'border-border-stroke-subtle bg-background-base-neutral flex border-spacing-[100px] select-none flex-col items-center justify-center rounded-md border-2 border-dotted p-6',
          {
            'opacity-50': isDragActive,
          }
        )}
      >
        {value ? (
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faFileArrowUp} className="text-text-subtle pb-3 text-4xl" />
            <div className="flex items-center gap-2">
              <span className="text-text-subtle font-medium">{value.name}</span>
              <button
                type="button"
                className="text-text-info text-sm"
                onClick={() => onChange(null)}
              >
                {t('words.remove')}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-text-subtle flex flex-col items-center text-sm font-medium">
            <FontAwesomeIcon icon={faFileArrowUp} className="text-text-subtle pb-3 text-4xl" />
            <p>
              <span onClick={addFileButtonClicked} className="text-text-info cursor-pointer">
                {t('settings.playRights.upload')}
              </span>
              <span className="lowercase">
                {` ${t('words.or')} ${t('memberships.membershipImports.label_dragAndDrop')}`}
              </span>
              {` CSV `}
              <span className="lowercase">{t('memberships.membershipImports.label_upTo')}</span>
              {` 10MB`}
            </p>
          </div>
        )}
      </div>
      {isDragActive && (
        <div
          id="drop_zone"
          className="absolute left-0 top-0 h-full w-full"
          onDrop={handleDrop}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
        ></div>
      )}
    </div>
  )
}
